import { useEffect, useState } from 'react'
import {
  Bell,
  Mail,
  Send,
  RefreshCw,
  Server,
  ClipboardList,
  ShieldCheck
} from 'lucide-react'
import { api, partnerAdminApi, type Client } from '../services/api'

type LogEntry = {
  id: string
  title: string
  status: 'success' | 'error'
  detail: string
  timestamp: string
}

export function NotificationTesting() {
  const storedTenantId = localStorage.getItem('partner-admin.notifications.tenantId') || ''
  const storedClientId = localStorage.getItem('partner-admin.notifications.clientId') || ''
  const [tenantId, setTenantId] = useState('')
  const [clients, setClients] = useState<Client[]>([])
  const [clientsLoading, setClientsLoading] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [busy, setBusy] = useState<Record<string, boolean>>({})

  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('Teste SMTP Brevo')
  const [emailText, setEmailText] = useState('Email de teste via Brevo (SMTP).')

  const [notifTitle, setNotifTitle] = useState('Teste notificacao')
  const [notifMessage, setNotifMessage] = useState('Teste de email via notifications')
  const [notifPriority, setNotifPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium')

  const [deviceToken, setDeviceToken] = useState('')
  const [devicePlatform, setDevicePlatform] = useState<'ios' | 'android' | 'web'>('web')
  const [deviceName, setDeviceName] = useState('Browser')

  useEffect(() => {
    if (storedTenantId && !tenantId) {
      setTenantId(storedTenantId)
    }
    if (storedClientId && !selectedClientId) {
      setSelectedClientId(storedClientId)
    }
  }, [storedTenantId, storedClientId, tenantId, selectedClientId])

  useEffect(() => {
    if (tenantId) {
      localStorage.setItem('partner-admin.notifications.tenantId', tenantId)
    } else {
      localStorage.removeItem('partner-admin.notifications.tenantId')
    }
  }, [tenantId])

  useEffect(() => {
    if (selectedClientId) {
      localStorage.setItem('partner-admin.notifications.clientId', selectedClientId)
    } else {
      localStorage.removeItem('partner-admin.notifications.clientId')
    }
  }, [selectedClientId])

  const addLog = (title: string, status: 'success' | 'error', detail: string) => {
    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      title,
      status,
      detail,
      timestamp: new Date().toLocaleString()
    }
    setLogs((prev) => [entry, ...prev].slice(0, 50))
  }

  const setLoading = (key: string, value: boolean) => {
    setBusy((prev) => ({ ...prev, [key]: value }))
  }

  const loadClients = async () => {
    setClientsLoading(true)
    try {
      const response = await partnerAdminApi.clients.list({ page: 1, limit: 200 })
      setClients(response.clients || [])
      if (!selectedClientId && response.clients?.length) {
        setSelectedClientId(response.clients[0].id)
        setTenantId(response.clients[0].id)
      }
    } catch (error: any) {
      addLog('Carregar clientes', 'error', error.response?.data?.message || error.message || 'Erro ao carregar clientes')
    } finally {
      setClientsLoading(false)
    }
  }

  const requireTenant = (actionLabel: string): boolean => {
    if (!tenantId.trim()) {
      addLog(actionLabel, 'error', 'Tenant ID obrigatorio (header x-tenant-id).')
      return false
    }
    return true
  }

  const handleSendEmail = async () => {
    const label = 'Enviar email direto'
    if (!requireTenant(label)) return
    setLoading('email', true)
    try {
      const response = await api.post(
        '/communications/send-email',
        {
          to: emailTo,
          subject: emailSubject,
          text: emailText
        },
        {
          headers: {
            'x-tenant-id': tenantId.trim()
          }
        }
      )
      addLog(label, 'success', JSON.stringify(response.data))
    } catch (error: any) {
      addLog(label, 'error', error.response?.data?.message || error.message || 'Erro ao enviar email')
    } finally {
      setLoading('email', false)
    }
  }

  const handlePartnerTestNotification = async () => {
    const label = 'Notificacao teste (partner)'
    setLoading('partner-test', true)
    try {
      const response = await api.post('/partner-admin/notifications/test', {
        title: notifTitle,
        message: notifMessage,
        priority: notifPriority
      })
      addLog(label, 'success', JSON.stringify(response.data))
    } catch (error: any) {
      addLog(label, 'error', error.response?.data?.message || error.message || 'Erro no teste')
    } finally {
      setLoading('partner-test', false)
    }
  }

  const handleRegisterDeviceToken = async () => {
    const label = 'Registrar device token'
    setLoading('device-token', true)
    try {
      const response = await api.post('/partner-admin/notifications/device-token', {
        token: deviceToken,
        platform: devicePlatform,
        device_name: deviceName
      })
      addLog(label, 'success', JSON.stringify(response.data))
    } catch (error: any) {
      addLog(label, 'error', error.response?.data?.message || error.message || 'Erro ao registrar token')
    } finally {
      setLoading('device-token', false)
    }
  }

  const handleListDeviceTokens = async () => {
    const label = 'Listar device tokens'
    setLoading('device-tokens', true)
    try {
      const response = await api.get('/partner-admin/notifications/device-tokens')
      addLog(label, 'success', JSON.stringify(response.data))
    } catch (error: any) {
      addLog(label, 'error', error.response?.data?.message || error.message || 'Erro ao listar tokens')
    } finally {
      setLoading('device-tokens', false)
    }
  }

  const handlePushStatus = async () => {
    const label = 'Push status'
    setLoading('push-status', true)
    try {
      const response = await api.get('/notifications/push-status')
      addLog(label, 'success', JSON.stringify(response.data))
    } catch (error: any) {
      addLog(label, 'error', error.response?.data?.message || error.message || 'Erro ao consultar push')
    } finally {
      setLoading('push-status', false)
    }
  }

  const clearLogs = () => setLogs([])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Testes de Notificacoes</h1>
        <p className="text-gray-600 mt-1">
          Botoes rapidos para disparar emails, push, SMS e notificacoes do Partner Admin.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <select
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value)
                    setTenantId(e.target.value)
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Selecione um cliente</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.slug})
                    </option>
                  ))}
                </select>
                <button
                  onClick={loadClients}
                  disabled={clientsLoading}
                  className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm disabled:opacity-60"
                >
                  {clientsLoading ? 'Carregando...' : 'Carregar clientes'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                O tenant ID sera preenchido automaticamente ao escolher um cliente.
              </p>
            </div>
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID (x-tenant-id)</label>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="UUID do tenant"
            />
            <p className="text-xs text-gray-500 mt-1">
              Necessario para /communications e /notifications/sms.
            </p>
            </div>
          </div>
          <button
            onClick={clearLogs}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
          >
            Limpar logs
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Email direto</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <input
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="destino@email.com"
            />
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Assunto"
            />
            <textarea
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[90px]"
              placeholder="Mensagem"
            />
          </div>
          <button
            onClick={handleSendEmail}
            disabled={busy.email}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {busy.email ? 'Enviando...' : 'Enviar email'}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold text-gray-900">Teste Partner Admin</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <input
              type="text"
              value={notifTitle}
              onChange={(e) => setNotifTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Titulo"
            />
            <textarea
              value={notifMessage}
              onChange={(e) => setNotifMessage(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[90px]"
              placeholder="Mensagem"
            />
            <select
              value={notifPriority}
              onChange={(e) => setNotifPriority(e.target.value as typeof notifPriority)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="low">Baixa</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </select>
          </div>
          <button
            onClick={handlePartnerTestNotification}
            disabled={busy['partner-test']}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Bell className="w-4 h-4" />
            {busy['partner-test'] ? 'Enviando...' : 'Disparar notificacao'}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-violet-600" />
            <h2 className="text-lg font-semibold text-gray-900">Device Token (push)</h2>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <input
              type="text"
              value={deviceToken}
              onChange={(e) => setDeviceToken(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="FCM/APNs token"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select
                value={devicePlatform}
                onChange={(e) => setDevicePlatform(e.target.value as typeof devicePlatform)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Nome do dispositivo"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleRegisterDeviceToken}
              disabled={busy['device-token']}
              className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
            >
              <ShieldCheck className="w-4 h-4" />
              {busy['device-token'] ? 'Registrando...' : 'Registrar token'}
            </button>
            <button
              onClick={handleListDeviceTokens}
              disabled={busy['device-tokens']}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-60"
            >
              <ClipboardList className="w-4 h-4" />
              {busy['device-tokens'] ? 'Carregando...' : 'Listar tokens'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-orange-600" />
            <h2 className="text-lg font-semibold text-gray-900">Status</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handlePushStatus}
              disabled={busy['push-status']}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw className="w-4 h-4" />
              {busy['push-status'] ? 'Consultando...' : 'Push status'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Logs recentes</h2>
        </div>
        {logs.length === 0 ? (
          <div className="text-sm text-gray-500">Nenhum log ainda.</div>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => (
              <div
                key={log.id}
                className={`border rounded-lg px-3 py-2 text-sm ${
                  log.status === 'success'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold">{log.title}</span>
                  <span className="text-xs opacity-70">{log.timestamp}</span>
                </div>
                <div className="mt-1 break-all">{log.detail}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
